<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { browser } from '$app/environment';
  import { darkMode } from '$lib/stores/theme-store';
  import { Moon, Sun, Menu, LogIn, LogOut, User, Search, X, Settings, Twitter } from 'lucide-svelte';
  import LoginPopup from './LoginPopup.svelte';
  import LanguageSwitcher from './LanguageSwitcher.svelte';
  import { fade } from 'svelte/transition';
  import { fly } from 'svelte/transition';
  import { authStore, signOut } from '$lib/stores/authStore';
  import { supabase } from '$lib/supabase';
  import { _, locale } from 'svelte-i18n';
  import { t as translate } from '$lib/i18n';
  
  // shadcn/svelteのコンポーネントをインポート
  import {
    Button,
    buttonVariants
  } from '$lib/components/shadcn/ui/button';
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
  } from "$lib/components/shadcn/ui/dialog";
  import { Input } from "$lib/components/shadcn/ui/input";
  
  export let toggleSidebar: () => void;

  let showLoginPopup = false;
  let userMenuOpen = false;

  let searchQuery = '';
  let isSearchOpen = false;
  let isDesktop: boolean | null = null;
  let suggestions: string[] = [];
  let showSuggestions = false;
  let searchTimeout: NodeJS.Timeout;

  let searchHistory: string[] = [];
  let showSearchHistory = false;

  let themeMode: 'light' | 'dark' | 'system' = 'system';
  
  let dialogOpen = false;
  
  // カスタムSvelteコンポーネントとして定義
  const SystemThemeIcon = {
    render: ($$props: { size?: number }) => {
      return `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="${$$props.size || 20}"
          height="${$$props.size || 20}"
          viewBox="0 0 24 24"
          class="relative"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          />
          <path
            d="M12 2a10 10 0 0 1 0 20V2Z"
            fill="currentColor"
          />
        </svg>
      `;
    }
  };
  
  const themeOptions = [
    { value: 'light', label: 'ライトモード', Icon: Sun },
    { value: 'dark', label: 'ダークモード', Icon: Moon },
    { value: 'system', label: '端末の設定に従う', Icon: SystemThemeIcon }
  ] as const;

  function setThemeMode(mode: 'light' | 'dark' | 'system') {
    themeMode = mode;
    if (mode === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      darkMode.set(prefersDark);
      localStorage.setItem('themeMode', 'system');
    } else {
      darkMode.set(mode === 'dark');
      localStorage.setItem('themeMode', mode);
    }
  }

  onMount(() => {
    if (browser) {
      const storedThemeMode = localStorage.getItem('themeMode') as 'light' | 'dark' | 'system' || 'dark';
      themeMode = storedThemeMode;
      
      if (storedThemeMode === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        darkMode.set(prefersDark);
      } else {
        darkMode.set(storedThemeMode === 'dark');
      }

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        if (themeMode === 'system') {
          darkMode.set(e.matches);
        }
      };
      mediaQuery.addEventListener('change', handleChange);

      const storedHistory = localStorage.getItem('searchHistory');
      if (storedHistory) {
        searchHistory = JSON.parse(storedHistory);
      }

      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  });

  $: if (browser) {
    document.documentElement.classList.toggle('dark', $darkMode);
  }

  function toggleLoginPopup() {
    showLoginPopup = !showLoginPopup;
  }

  function toggleUserMenu() {
    userMenuOpen = !userMenuOpen;
  }

  $: user = $authStore.user;
  $: userInitial = user?.email ? user.email[0].toUpperCase() : '';

  async function handleSignOut() {
    const success = await signOut();
    if (success && browser) {
      window.location.reload();
    }
  }

  async function fetchSuggestions(query: string) {
    if (query.length < 2) {
      suggestions = [];
      showSuggestions = false;
      return;
    }

    const { data: videos } = await supabase
      .from('video')
      .select('title')
      .order('published_at', { ascending: false })
      .limit(1000);

    if (!videos) {
      suggestions = [];
      return;
    }

    const words = new Set<string>();
    videos.forEach(video => {
      const matches = video.title.toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
      matches.forEach((word: string) => {
        if (word.toLowerCase().includes(query.toLowerCase()) && word.length > 1) {
          words.add(word);
        }
      });
    });

    suggestions = Array.from(words)
      .slice(0, 10)
      .sort((a, b) => a.length - b.length);
    showSuggestions = suggestions.length > 0;
  }

  function handleInput() {
    showSearchHistory = false;
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 300);
  }

  function saveSearchHistory(query: string) {
    searchHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 3);
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }

  function removeSearchHistoryItem(item: string) {
    searchHistory = searchHistory.filter(i => i !== item);
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }

  function handleSuggestionClick(suggestion: string) {
    searchQuery = suggestion;
    showSuggestions = false;
    saveSearchHistory(suggestion);
    window.location.href = `/search?q=${encodeURIComponent(suggestion)}`;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && searchQuery) {
      showSuggestions = false;
      saveSearchHistory(searchQuery);
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  }

  function handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-container')) {
      showSuggestions = false;
    }
  }

  function toggleSearch() {
    isSearchOpen = !isSearchOpen;
  }

  function checkScreenSize() {
    isDesktop = window.innerWidth >= 768;
  }

  onMount(() => {
    if (browser) {
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
    }
  });

  onDestroy(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  function handleFocus() {
    if (!searchQuery) {
      showSearchHistory = true;
    }
  }

  function handleBlur() {
    setTimeout(() => {
      showSearchHistory = false;
    }, 200);
  }

  function openThemeModal() {
    dialogOpen = true;
  }

  function closeThemeModal() {
    dialogOpen = false;
  }
</script>

<svelte:window on:click={handleClickOutside} />

<header class="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b z-50 fixed top-0 left-0 right-0">
  <div class="flex h-16 items-center">
    <div class="flex items-center mr-4">
      <Button variant="ghost" size="icon" on:click={toggleSidebar} class="ml-2 mr-2">
        <Menu class="h-5 w-5" />
        <span class="sr-only">トグルサイドバー</span>
      </Button>
      
      <a href="/" class="text-base md:text-lg font-bold text-foreground transition-colors duration-200 modern-font flex items-center">
        <span class="bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-500 bg-clip-text text-transparent font-extrabold tracking-tight">VT</span><span class="font-medium tracking-wide">Grid</span>
      </a>
    </div>

    <div class="flex-1"></div>
    
    <nav class="flex items-center gap-4 pr-4">
      <div class="relative search-container">
        {#if isDesktop === true}
          <div class="relative w-full md:w-64 lg:w-80">
            <div class="relative">
              <Search class="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={translate('header.search_placeholder', '動画を検索...')}
                class="pl-10"
                bind:value={searchQuery}
                on:input={handleInput}
                on:keydown={handleKeydown}
                on:focus={handleFocus}
                on:blur={handleBlur}
              />
            </div>
            
            {#if showSearchHistory || showSuggestions}
              <div class="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md overflow-hidden">
                {#if showSearchHistory && searchHistory.length > 0}
                  <div class="px-2 py-1.5 text-xs text-muted-foreground">{translate('header.recent_searches', '最近の検索')}</div>
                  {#each searchHistory as historyItem}
                    <div class="flex items-center justify-between px-2 py-1.5 hover:bg-muted">
                      <button
                        on:click={() => handleSuggestionClick(historyItem)}
                        class="w-full text-left text-sm text-foreground"
                      >
                        {historyItem}
                      </button>
                      <Button variant="ghost" size="icon" class="h-6 w-6" on:click={() => removeSearchHistoryItem(historyItem)}>
                        <X class="h-3 w-3" />
                        <span class="sr-only">削除</span>
                      </Button>
                    </div>
                  {/each}
                {/if}
                {#if showSuggestions}
                  {#each suggestions as suggestion}
                    <button
                      on:click={() => handleSuggestionClick(suggestion)}
                      class="w-full px-2 py-1.5 text-left text-sm hover:bg-muted text-foreground"
                    >
                      {suggestion}
                    </button>
                  {/each}
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <div class="flex items-center space-x-1">
        <Button
          variant="ghost"
          size="icon"
          on:click={openThemeModal}
          aria-label={translate('header.change_theme', 'テーマを変更')}
        >
          {#if themeMode === 'light'}
            <Sun class="h-5 w-5" />
          {:else if themeMode === 'dark'}
            <Moon class="h-5 w-5" />
          {:else}
            {@html SystemThemeIcon.render({ size: 20 })}
          {/if}
        </Button>

        <div class="flex flex-col items-center">
          <LanguageSwitcher />
        </div>
        
        {#if user}
          <a href="/mypage" class="flex items-center">
            <Button variant="outline" class="gap-2">
              <div class="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-xs">
                {userInitial}
              </div>
              <span class="hidden sm:inline-block">{translate('header.my_page', 'マイページ')}</span>
            </Button>
          </a>
        {:else}
          <Button variant="outline" size="sm" on:click={toggleLoginPopup}>
            <LogIn class="mr-2 h-4 w-4" />
            <span>{translate('common.actions.login', 'ログイン')}</span>
          </Button>
        {/if}
      </div>
    </nav>
  </div>
</header>

{#if showLoginPopup}
  <LoginPopup on:close={toggleLoginPopup} />
{/if}

<!-- テーマ選択モーダル -->
<Dialog bind:open={dialogOpen}>
  <DialogContent class="sm:max-w-md">
    <DialogHeader>
      <DialogTitle>{translate('header.theme_settings', 'テーマ設定')}</DialogTitle>
      <DialogDescription>
        {translate('header.theme_description', '画面の表示モードを選択してください')}
      </DialogDescription>
    </DialogHeader>
    <div class="grid gap-2">
      {#each themeOptions as option}
        <Button
          variant={themeMode === option.value ? "default" : "outline"}
          class="justify-start {themeMode === option.value ? 'dark:text-white' : ''}"
          on:click={() => {
            setThemeMode(option.value);
            closeThemeModal();
          }}
        >
          <div class="flex items-center gap-2">
            {#if option.value === 'system'}
              {@html SystemThemeIcon.render({ size: 18 })}
            {:else}
              <svelte:component this={option.Icon} size={18} />
            {/if}
            <span>{option.label}</span>
          </div>
          {#if themeMode === option.value}
            <div class="ml-auto flex h-5 w-5 items-center justify-center">
              <div class="h-2 w-2 rounded-full bg-white dark:bg-white" />
            </div>
          {/if}
        </Button>
      {/each}
    </div>
    <DialogFooter>
      <Button variant="outline" on:click={closeThemeModal}>
        {translate('common.actions.close', '閉じる')}
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>

<style>
  .modern-font {
    font-family: 'Outfit', sans-serif;
    letter-spacing: 0.01em;
  }

  /* スクロールバーを非表示にする */
  :global(.no-scrollbar) {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  :global(.no-scrollbar::-webkit-scrollbar) {
    display: none;
  }
</style>
